import classNames from 'classnames';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import ArrowPrev from '@/ui-assets/icons/arrow-prev.svg?react';
import NavClose from '@/ui-assets/icons/nav-close.svg?react';
import { onKeyDownHandler } from '@/utils/a11y';
import { trackEvent } from '@/utils/analytics';

import FlipOnRtl from '../FlipOnRtl';

import styles from './index.module.scss';

type Props = {
  readonly title?: string;
  readonly type?: 'back' | 'close';
  readonly isHidden?: boolean;
  readonly onClose?: () => void;
  readonly onSkip?: () => void;
};

const NavBar = ({ title, type = 'back', isHidden, onClose, onSkip }: Props) => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const isClosable = type === 'close';

  const clickHandler = useCallback(() => {
    // Only track if onClose is not provided (NavBar handles navigation itself)
    if (!onClose) {
      trackEvent(
        'nav_bar',
        'closed',
        { eventId: 'nav_bar_close_clicked' },
        { type: isClosable ? 'close' : 'back' }
      );
    }

    if (onClose) {
      onClose();

      return;
    }

    if (isClosable) {
      window.close();

      return;
    }

    navigate(-1);
  }, [isClosable, navigate, onClose]);

  return (
    <nav className={classNames(styles.navBar, isHidden && styles.hidden)}>
      <div
        role="button"
        tabIndex={0}
        className={styles.navButton}
        onKeyDown={onKeyDownHandler(clickHandler)}
        onClick={clickHandler}
      >
        {isClosable ? (
          <NavClose />
        ) : (
          <FlipOnRtl>
            <ArrowPrev />
          </FlipOnRtl>
        )}
        {!isClosable && <span>{t('action.nav_back')}</span>}
      </div>
      {title && <div className={styles.title}>{title}</div>}
      {onSkip && (
        <div
          role="button"
          tabIndex={0}
          className={styles.skipButton}
          onKeyDown={onKeyDownHandler(() => {
            trackEvent(
              'nav_bar',
              'skipped',
              { eventId: 'nav_bar_skip_clicked' },
              {}
            );
            onSkip();
          })}
          onClick={() => {
            trackEvent(
              'nav_bar',
              'skipped',
              { eventId: 'nav_bar_skip_clicked' },
              {}
            );
            onSkip();
          }}
        >
          <span>{t('action.nav_skip')}</span>
        </div>
      )}
    </nav>
  );
};

export default NavBar;
